<template lang="pug">
div
  v-progress-linear(v-if='loading' indeterminate color='#18bedf')
  v-alert(v-else-if='loadError' type='error' dense text) {{ loadError }}
  template(v-else)
    .d-flex
      v-checkbox.mt-0.mr-8(
        v-model='form.knowledge'
        :label=`$t('TENANT_PIPELINE.ENABLE_PORTAL_KNOWLEDGE')`
        :disabled='state.has_knowledge_index || busy'
        color='#18bedf'
        hide-details
        @change='onOptionChange'
      )
      v-checkbox.mt-0(
        v-model='form.image'
        :label=`$t('TENANT_PIPELINE.ENABLE_IMAGE_SEARCH')`
        :disabled='state.has_image_index || busy'
        color='#18bedf'
        hide-details
        @change='onOptionChange'
      )
    v-row.mt-4(no-gutters)
      v-col.pr-2(cols='6')
        v-text-field(v-if='form.knowledge' v-model='form.storageSize' :label=`$t('TENANT_PIPELINE.STORAGE_SIZE')` type='number' :min='0' outlined dense hide-details='auto' :rules='[quotaRule]' :disabled='busy' @input='emitStatus')
      v-col.pl-2(cols='6')
        v-text-field(v-if='form.image' v-model='form.imageSearchStore' :label=`$t('TENANT_PIPELINE.IMAGE_SEARCH_STORE')` type='number' :min='0' outlined dense hide-details='auto' :rules='[quotaRule]' :disabled='busy' @input='emitStatus')
    template(v-if='form.knowledge')
      .mt-4(v-if='state.portal_bot_name' style='font-size: 14px; color:#444444;') {{$t('TENANT_PIPELINE.PORTAL_BOT_EXISTS', { name: state.portal_bot_name })}}
      v-alert.mt-4.mb-0(v-else-if='!state.portal_bot_sources.length' type='warning' dense text) {{$t('TENANT_PIPELINE.NO_PORTAL_BOT_SOURCE')}}
      v-select.mt-4(
        v-else
        v-model='form.portalBotSource'
        :items='portalBotSourceItems'
        :label=`$t('TENANT_PIPELINE.PORTAL_BOT_SOURCE')`
        :rules='[portalBotSourceRule]'
        :disabled='busy'
        :clearable='!botSourceRequired'
        outlined
        dense
        hide-details='auto'
        @change='emitStatus'
      )
    .mt-5.mb-2(style='color:#444444; font-weight: 500; font-size: 14px;') {{$t('TENANT_PIPELINE.SEARCH_SERVICE')}}
    v-radio-group.mt-0(v-model='form.serviceId' hide-details @change='emitStatus')
      v-card.mb-2.pa-2(v-for='service in state.services' :key='service.id' outlined)
        .d-flex.align-center
          v-radio.mb-0(:value='service.id' :disabled='!serviceSelectable(service)' color='#18bedf')
          .flex-grow-1(style='min-width: 0;')
            .text-truncate(style='font-size: 14px; color:#222222;' :title='service.endpoint') {{ service.endpoint }}
            div(style='font-size: 12px; color:#888888;')
              span(v-if='service.source === "env"') .env
              span(v-else) {{$t('TENANT_PIPELINE.FROM_PREFERENCE', { tenant: service.source_tenant })}}
          .ml-2(v-if='service.reachable' style='font-size: 14px; white-space: nowrap;' :style='{ color: isFull(service) ? "#e53935" : "#444444" }') {{ service.index_count }}/{{ service.index_quota === null ? '-' : service.index_quota }}
          .ml-2(v-else style='font-size: 13px; color:#e53935; white-space: nowrap;' :title='service.error') {{$t('TENANT_PIPELINE.UNREACHABLE')}}
          v-btn.ml-1(icon small :disabled='!service.reachable' @click='toggleExpand(service.id)')
            v-icon {{ expanded.includes(service.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        v-expand-transition
          .pl-10.pt-2(v-if='expanded.includes(service.id)')
            v-simple-table(dense)
              thead
                tr
                  th {{$t('TENANTCENTER_TENANT_LIST.SCHEMANAME')}}
                  th {{$t('TENANTCENTER_TENANT_LIST.DESCRIPTION')}}
                  th {{$t('TENANTCENTER_TENANT_LIST.EXPIRATION')}}
                  th Index
              tbody
                tr(v-for='group in tenantGroups(service)' :key='group.schema_name')
                  td {{ group.schema_name }}
                  td {{ group.description }}
                  td(style='white-space: nowrap;') {{ group.subscription_end_date ? new Date(group.subscription_end_date).toLocaleDateString() : '' }}
                  td
                    v-chip.mr-1(v-for='usage in group.usages' :key='usage' x-small) {{ usageLabel(usage) }}
                tr(v-for='index in orphanIndexes(service)' :key='`orphan-${index.name}`')
                  td(style='color:#888888;') {{ index.name }}
                  td(style='color:#888888;') {{$t('TENANT_PIPELINE.ORPHAN_INDEX')}}
                  td
                  td
</template>

<script>
import Vue from "vue";
import i18n from '/common/plugins/vue-i18n.js';
import TenantCenterService from "@/services/TenantCenterService";
import { errorText } from "@/components/TenantCenter/Pipeline/pipelineUtils";

export default Vue.extend({
  props: {
    tenant: { type: Object, require: true },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      loadError: '',
      state: null,
      form: { knowledge: false, image: false, serviceId: null, storageSize: 0, imageSearchStore: 0, portalBotSource: null },
      expanded: [],
      quotaRule: (v) => /^\d+$/.test(String(v)) || i18n.t('RULE.RULE_ALPHA_NUMERIC'),
    };
  },
  computed: {
    existingServiceId() {
      return this.state ? (this.state.knowledge_service_id || this.state.image_service_id) : null;
    },
    wantsNewIndex() {
      return (this.form.knowledge && !this.state.has_knowledge_index) || (this.form.image && !this.state.has_image_index);
    },
    // 勾選知識庫、此租戶還沒有 Portal bot，且有租戶可供複製時才顯示來源選單
    canPickBotSource() {
      return this.form.knowledge && !this.state.portal_bot_name && this.state.portal_bot_sources.length > 0;
    },
    // 新開知識庫時必選；已有知識庫 index 只是補 bot 則可不選
    botSourceRequired() {
      return this.canPickBotSource && !this.state.has_knowledge_index;
    },
    portalBotSourceItems() {
      return this.state.portal_bot_sources.map((s) => ({
        value: s.schema_name,
        text: `${s.schema_name}${s.description ? ` - ${s.description}` : ''}（${s.bot_name}）`,
      }));
    },
  },
  async created() {
    try {
      const response = await TenantCenterService.pipelineGetStage(this.tenant.schema_name, 'search');
      this.applyState(response.data);
    } catch (error) {
      this.loadError = errorText(error);
    } finally {
      this.loading = false;
      this.emitStatus();
    }
  },
  methods: {
    applyState(state) {
      this.state = state;
      this.form = {
        knowledge: state.has_knowledge_index,
        image: state.has_image_index,
        serviceId: this.existingServiceId,
        storageSize: state.quota.storage_size,
        imageSearchStore: state.quota.image_search_store,
        portalBotSource: null,
      };
    },
    portalBotSourceRule(v) {
      return !this.botSourceRequired || !!v || i18n.t('RULE.RULE_R');
    },
    isFull(service) {
      return service.index_quota !== null && service.index_count >= service.index_quota;
    },
    serviceSelectable(service) {
      // 已有 index：服務固定；尚未勾選任何項目：無須選擇
      if (this.busy || this.existingServiceId || !this.wantsNewIndex) {
        return false;
      }
      return service.reachable && !this.isFull(service);
    },
    onOptionChange() {
      if (!this.existingServiceId) {
        if (!this.wantsNewIndex) {
          this.form.serviceId = null;
        } else if (!this.form.serviceId) {
          // 預設選租戶目前連線設定所指的服務
          const configured = this.state.services.find((s) => s.id === this.state.configured_service_id);
          if (configured && this.serviceSelectable(configured)) {
            this.form.serviceId = configured.id;
          }
        }
      }
      this.emitStatus();
    },
    quotaChanged() {
      return (this.form.knowledge && String(this.form.storageSize) !== String(this.state.quota.storage_size))
        || (this.form.image && String(this.form.imageSearchStore) !== String(this.state.quota.image_search_store));
    },
    emitStatus() {
      if (this.loading || this.loadError) {
        this.$emit('status', { dirty: false, ready: !this.loading });
        return;
      }
      const pickedBotSource = this.canPickBotSource && !!this.form.portalBotSource;
      const dirty = this.wantsNewIndex || this.quotaChanged() || pickedBotSource;
      const quotaValid = (!this.form.knowledge || this.quotaRule(this.form.storageSize) === true)
        && (!this.form.image || this.quotaRule(this.form.imageSearchStore) === true);
      const serviceValid = !this.wantsNewIndex || !!this.form.serviceId;
      const botSourceValid = this.portalBotSourceRule(this.form.portalBotSource) === true;
      this.$emit('status', { dirty: dirty, ready: !dirty || (quotaValid && serviceValid && botSourceValid) });
    },
    toggleExpand(serviceId) {
      this.expanded = this.expanded.includes(serviceId)
        ? this.expanded.filter((id) => id !== serviceId)
        : [...this.expanded, serviceId];
    },
    tenantGroups(service) {
      const groups = {};
      service.indexes.filter((index) => index.schema_name).forEach((index) => {
        if (!groups[index.schema_name]) {
          groups[index.schema_name] = {
            schema_name: index.schema_name,
            description: index.description,
            subscription_end_date: index.subscription_end_date,
            usages: [],
          };
        }
        groups[index.schema_name].usages.push(index.usage);
      });
      return Object.values(groups).sort((a, b) => a.schema_name.localeCompare(b.schema_name));
    },
    orphanIndexes(service) {
      return service.indexes.filter((index) => !index.schema_name);
    },
    usageLabel(usage) {
      return usage === 'image' ? i18n.t('TENANT_PIPELINE.ENABLE_IMAGE_SEARCH') : i18n.t('TENANT_PIPELINE.ENABLE_PORTAL_KNOWLEDGE');
    },
    async submit() {
      this.$emit('progress', i18n.t('TENANT_PIPELINE.UPDATING'));
      try {
        const response = await TenantCenterService.pipelineUpdateStage(this.tenant.schema_name, 'search', {
          service_id: this.form.serviceId,
          knowledge: this.form.knowledge,
          image: this.form.image,
          portal_bot_source: this.canPickBotSource ? this.form.portalBotSource : null,
          quota: {
            storage_size: Number(this.form.storageSize),
            image_search_store: Number(this.form.imageSearchStore),
          },
        });
        this.applyState(response.data);
      } catch (error) {
        throw new Error(errorText(error));
      } finally {
        this.emitStatus();
      }
    },
  },
});
</script>
